import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BuscarClientesRow } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import {
  atualizarCliente, buscarClientes, criarCliente, historicoDoCliente,
  salvarVeiculoCliente, veiculosDoCliente,
} from '../lib/consultas';
import {
  formatarDocumento, formatarTelefone, problemaNoCliente,
} from '../lib/cliente';
import { brl, dataBR, dataHoraBR, hojeISO } from '../lib/formato';
import { validadePadrao } from '../lib/orcamento';
import { mensagemDeErro } from '../lib/supabase';
import { podeVerFaturamento, useSessao } from '../lib/sessao';

// Tela de clientes (migração 023) — gerente e dono.
//
// O que ela responde, e que nenhuma outra tela responde: "quem é essa pessoa,
// o que ela tem rodando e como se fala com ela". Telefone, CPF, carros e placa
// são trabalho de gerência: é com isso que se cobra e se oferece a peça certa.
//
// O BLOCO "COMPRAS" É SÓ DO DONO desde 28/09/2026 — quantas compras, quanto o
// cliente já gastou e QUANDO foi a última é faturamento visto por outro
// ângulo, e o dono mandou tirar isso da equipe. O histórico vem de
// historico_do_cliente(), RPC definer que atravessa a policy de `sales` de
// propósito: ele soma as vendas de TODA a loja para aquele cliente. Para quem
// não é dono a consulta nem é feita — o componente não entra na tela.
//
// A DATA DA ÚLTIMA COMPRA saiu junto, e é o detalhe que a primeira versão
// errou. Ela parecia inofensiva ("não soma nada"), e era pior: vem da MESMA
// consulta que a contagem (buscar_clientes é security invoker), e a policy nova
// de `sales` só dá ao gerente as vendas DELE. Para o cliente que comprou com um
// colega, `ultima_compra` chega NULA e o cartão escrevia "nunca comprou" na
// ficha de quem compra toda semana. Dado errado é pior que dado ausente.
//
// Cadastrar é de todo mundo (o vendedor cadastra na tela de Venda); ESTA tela
// é a de gestão: pesquisar, corrigir cadastro, a frota e — para o dono — o
// histórico de compras.

export default function Clientes() {
  const { papel } = useSessao();
  // O cartão não decide permissão: recebe pronto, como em Vendas.
  const verFaturamento = podeVerFaturamento(papel);
  const [texto, setTexto] = useState('');
  const [termo, setTermo] = useState('');
  const [aberto, setAberto] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const cliente = useQueryClient();

  useEffect(() => {
    const t = setTimeout(() => setTermo(texto), 250);
    return () => clearTimeout(t);
  }, [texto]);

  // Termo vazio devolve os primeiros em ordem alfabética: a tela abre com a
  // lista em vez de um vazio que parece defeito.
  const { data, isLoading, error } = useQuery({
    queryKey: ['clientes', termo],
    queryFn: () => buscarClientes(termo, 50),
    staleTime: 30_000,
  });

  const recarregar = () => cliente.invalidateQueries({ queryKey: ['clientes'] });

  return (
    <Layout
      titulo="Clientes"
      acao={
        <button type="button" onClick={() => setCriando((v) => !v)}
                className="botao-primario px-3 py-2 text-xs">
          {criando ? 'Fechar' : '+ Novo'}
        </button>
      }
    >
      <div className="space-y-4">
        {criando && (
          <NovoClienteForm aoCriar={() => { setCriando(false); recarregar(); }} />
        )}

        <input
          className="campo py-3 text-lg"
          value={texto}
          placeholder="Nome, CPF ou telefone…"
          autoFocus
          onChange={(e) => setTexto(e.target.value)}
        />

        {error ? <Aviso tipo="erro">{mensagemDeErro(error)}</Aviso> : null}
        {isLoading && <Carregando texto="Carregando clientes" />}

        {data && data.length > 0 && (
          <ul className="space-y-2">
            {data.map((c) => (
              <li key={c.id}>
                <CartaoCliente
                  cliente={c}
                  verFaturamento={verFaturamento}
                  aberto={aberto === c.id}
                  aoAbrir={() => setAberto(aberto === c.id ? null : c.id)}
                  aoMudar={recarregar}
                />
              </li>
            ))}
          </ul>
        )}

        {data && data.length === 0 && !isLoading && (
          <div className="cartao p-6 text-center text-sm text-mute">
            {termo
              ? `Nenhum cliente para "${termo}".`
              : 'Nenhum cliente cadastrado ainda. Eles entram sozinhos conforme o balcão vende — ou use "+ Novo".'}
          </div>
        )}
      </div>
    </Layout>
  );
}

function NovoClienteForm({ aoCriar }: { aoCriar: () => void }) {
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [telefone, setTelefone] = useState('');
  const [observacao, setObservacao] = useState('');

  const criar = useMutation({
    mutationFn: async () => {
      const problema = problemaNoCliente({ nome, cpf, telefone });
      if (problema) throw new Error(problema);
      return criarCliente({ nome, cpf, telefone, observacao });
    },
    onSuccess: aoCriar,
  });

  return (
    <section className="cartao space-y-3 border-signal/50 p-4">
      <h2 className="text-base font-bold">Cliente novo</h2>
      <div>
        <label className="rotulo" htmlFor="cl-nome">Nome *</label>
        <input id="cl-nome" className="campo" value={nome} autoFocus
               onChange={(e) => setNome(e.target.value)} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor="cl-cpf">CPF / CNPJ</label>
          <input id="cl-cpf" className="campo font-mono" inputMode="numeric" value={cpf}
                 placeholder="000.000.000-00"
                 onChange={(e) => setCpf(e.target.value)}
                 onBlur={() => setCpf(formatarDocumento(cpf))} />
        </div>
        <div>
          <label className="rotulo" htmlFor="cl-fone">Telefone</label>
          <input id="cl-fone" className="campo" inputMode="tel" value={telefone}
                 placeholder="(53) 99988-7766"
                 onChange={(e) => setTelefone(e.target.value)}
                 onBlur={() => setTelefone(formatarTelefone(telefone))} />
        </div>
      </div>
      <div>
        <label className="rotulo" htmlFor="cl-obs">Observação</label>
        <input id="cl-obs" className="campo" value={observacao}
               onChange={(e) => setObservacao(e.target.value)}
               placeholder="Ex.: frota da padaria, sempre pede nota" />
      </div>
      {criar.error ? <Aviso tipo="erro">{mensagemDeErro(criar.error)}</Aviso> : null}
      <button type="button" className="botao-primario w-full" disabled={criar.isPending}
              onClick={() => criar.mutate()}>
        {criar.isPending ? 'Salvando…' : 'Cadastrar'}
      </button>
    </section>
  );
}

/** Exportado para o teste: é aqui que o bloco de compras aparece ou não. */
export function CartaoCliente({ cliente: c, verFaturamento, aberto, aoAbrir, aoMudar }: {
  cliente: BuscarClientesRow;
  /** Quem está olhando pode ver quanto o cliente comprou? Só o dono. */
  verFaturamento: boolean;
  aberto: boolean; aoAbrir: () => void; aoMudar: () => void;
}) {
  const [editando, setEditando] = useState(false);

  return (
    <article className="cartao">
      <button type="button" onClick={aoAbrir}
              className="flex w-full items-center justify-between gap-3 p-4 text-left">
        <div className="min-w-0">
          <p className="truncate font-bold">{c.nome}</p>
          <p className="text-xs text-dim">
            {c.cpf ? formatarDocumento(c.cpf) : 'sem CPF'}
            {c.telefone ? ` · ${formatarTelefone(c.telefone)}` : ''}
          </p>
        </div>
        <div className="shrink-0 text-right">
          {/* CONTAGEM E DATA, as duas do dono (28/09/2026).
              A contagem é "quanto se vende" por cliente, e saiu na primeira
              versão. A DATA tinha ficado — parecia inofensiva, "não soma
              nada" — e era pior: as duas vêm da MESMA consulta
              (buscar_clientes, security invoker), e a policy nova de `sales`
              só dá ao gerente as vendas DELE. Para o cliente que comprou com
              um colega, `ultima_compra` chega NULA e o cartão escrevia
              "nunca comprou" na ficha de quem compra toda semana. Dado errado
              é pior que dado ausente: a ficha calada não engana ninguém. */}
          {verFaturamento && (
            <>
              <p className="text-sm font-bold">{c.compras} compra(s)</p>
              <p className="text-xs text-dim">
                {c.ultima_compra ? `última ${dataBR(c.ultima_compra)}` : 'nunca comprou'}
              </p>
            </>
          )}
        </div>
      </button>

      {aberto && (
        <div className="space-y-3 border-t border-edge p-4">
          {editando
            ? <EditarCliente cliente={c} aoFechar={() => setEditando(false)} aoSalvar={() => { setEditando(false); aoMudar(); }} />
            : (
              <button type="button" className="botao-secundario py-2 text-sm"
                      onClick={() => setEditando(true)}>
                Editar cadastro
              </button>
            )}
          <Frota customerId={c.id} />
          {/* O histórico de compras (quantas e quanto) é do dono. Ele NÃO
              entra na árvore para quem não pode ver: assim a RPC, que o banco
              nega, nem chega a ser chamada — erro vermelho no cartão do
              cliente seria confundido com defeito do cadastro. */}
          {verFaturamento && <Historico customerId={c.id} />}
        </div>
      )}
    </article>
  );
}

function EditarCliente({ cliente: c, aoSalvar, aoFechar }: {
  cliente: BuscarClientesRow; aoSalvar: () => void; aoFechar: () => void;
}) {
  const [nome, setNome] = useState(c.nome);
  const [cpf, setCpf] = useState(c.cpf ?? '');
  const [telefone, setTelefone] = useState(c.telefone ?? '');

  const salvar = useMutation({
    mutationFn: async () => {
      const problema = problemaNoCliente({ nome, cpf, telefone });
      if (problema) throw new Error(problema);
      return atualizarCliente(c.id, {
        nome: nome.trim(),
        cpf: cpf.trim() || null,
        telefone: telefone.trim() || null,
      });
    },
    onSuccess: aoSalvar,
  });

  return (
    <div className="space-y-3 border border-edge p-3">
      <p className="rotulo">Editar cadastro</p>
      <input className="campo py-2" value={nome} onChange={(e) => setNome(e.target.value)} />
      <div className="grid gap-2 sm:grid-cols-2">
        <input className="campo py-2 font-mono" value={cpf} placeholder="CPF/CNPJ"
               onChange={(e) => setCpf(e.target.value)}
               onBlur={() => setCpf(formatarDocumento(cpf))} />
        <input className="campo py-2" value={telefone} placeholder="Telefone"
               onChange={(e) => setTelefone(e.target.value)}
               onBlur={() => setTelefone(formatarTelefone(telefone))} />
      </div>
      <p className="text-xs text-dim">
        Corrigir o cadastro NÃO reescreve o histórico: o nome que saiu em cada venda ficou
        congelado nela.
      </p>
      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
          Voltar
        </button>
        <button type="button" className="botao-primario flex-1 py-2 text-sm"
                disabled={salvar.isPending} onClick={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}

function Historico({ customerId }: { customerId: string }) {
  // Período aberto por padrão (últimos 12 meses): a pergunta comum é "o que ele
  // comprou aqui", não "o que ele comprou em março".
  const [de, setDe] = useState(() => validadePadrao(hojeISO(), -365));
  const [ate, setAte] = useState(() => hojeISO());

  const { data, isLoading, error } = useQuery({
    queryKey: ['historico-cliente', customerId, de, ate],
    queryFn: () => historicoDoCliente({ customerId, de, ate }),
  });

  const validas = (data ?? []).filter((v) => !v.cancelada_em);
  const total = validas.reduce((s, v) => s + Number(v.total), 0);

  return (
    <div>
      <p className="rotulo">Compras</p>
      <div className="mb-3 grid grid-cols-2 gap-2">
        <div>
          <label className="rotulo" htmlFor={`de-${customerId}`}>De</label>
          <input id={`de-${customerId}`} type="date" className="campo py-2" value={de}
                 onChange={(e) => setDe(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor={`ate-${customerId}`}>Até</label>
          <input id={`ate-${customerId}`} type="date" className="campo py-2" value={ate}
                 onChange={(e) => setAte(e.target.value)} />
        </div>
      </div>

      {error ? <Aviso tipo="erro">{mensagemDeErro(error)}</Aviso> : null}
      {isLoading && <Carregando texto="Carregando histórico" />}

      {validas.length > 0 && (
        <div className="mb-2 flex items-baseline justify-between border-b border-edge pb-2">
          <span className="text-xs text-dim">{validas.length} compra(s) no período</span>
          <span className="text-lg font-black tabular-nums">{brl(total)}</span>
        </div>
      )}

      {data && data.length > 0 ? (
        <ul className="divide-y divide-edge">
          {data.map((v) => (
            <li key={v.sale_id} className={`py-2 ${v.cancelada_em ? 'opacity-50' : ''}`}>
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm">
                    {dataHoraBR(v.vendida_em)}
                    {v.cancelada_em && <span className="ml-1 text-xs text-erro">cancelada</span>}
                  </p>
                  <p className="text-xs text-dim">
                    {v.itens} item(ns)
                    {v.vendedor ? ` · ${v.vendedor}` : ''}
                    {v.forma_pagamento ? ` · ${v.forma_pagamento}` : ''}
                  </p>
                </div>
                <Link to={`/vendas`} className="shrink-0 text-right">
                  <span className={`font-bold tabular-nums ${v.cancelada_em ? 'line-through' : ''}`}>
                    {brl(Number(v.total))}
                  </span>
                </Link>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        !isLoading && <p className="py-2 text-sm text-dim">Nenhuma compra no período.</p>
      )}
    </div>
  );
}

/**
 * A FROTA do cliente (051). Para a locadora e o dono de frota, esta lista É o
 * cliente: é ela que responde "o que esse cara tem rodando?" — e é o que
 * permite oferecer a peça certa antes de ele pedir.
 *
 * O carro pode entrar SÓ com o modelo: no balcão, "um Gol 2012" já é
 * informação útil, e exigir a placa faria o vendedor não cadastrar nada.
 */
function Frota({ customerId }: { customerId: string }) {
  const cliente = useQueryClient();
  const [novo, setNovo] = useState(false);
  const vazio = {
    placa: '', montadora: '', modelo: '', ano: '', motorizacao: '', apelido: '',
  };
  const [f, setF] = useState(vazio);
  const set = <K extends keyof typeof vazio>(k: K, v: string) =>
    setF((a) => ({ ...a, [k]: v }));

  const carros = useQuery({
    queryKey: ['veiculos', customerId],
    queryFn: () => veiculosDoCliente(customerId),
  });

  const salvar = useMutation({
    mutationFn: () => salvarVeiculoCliente({
      customerId,
      placa: f.placa || null,
      montadora: f.montadora || null,
      modelo: f.modelo || null,
      ano: f.ano ? Number(f.ano) : null,
      motorizacao: f.motorizacao || null,
      apelido: f.apelido || null,
    }),
    onSuccess: () => {
      setNovo(false);
      setF(vazio);
      cliente.invalidateQueries({ queryKey: ['veiculos', customerId] });
    },
  });

  // "Vendeu" desativa o vínculo. O update da RPC é total, então os campos do
  // carro vão junto — sem isso ele perderia a identificação ao sair da frota.
  const desativar = useMutation({
    mutationFn: (id: string) => {
      const v = carros.data?.find((x) => x.id === id);
      return salvarVeiculoCliente({
        id, customerId, ativo: false,
        placa: v?.placa ?? null, montadora: v?.montadora ?? null,
        modelo: v?.modelo ?? null, ano: v?.ano ?? null,
        motorizacao: v?.motorizacao ?? null, apelido: v?.apelido ?? null,
      });
    },
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['veiculos', customerId] }),
  });

  return (
    <section className="border border-edge p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="rotulo">Carros ({carros.data?.length ?? 0})</h3>
        <button type="button" className="text-xs text-dim underline"
                onClick={() => setNovo((v) => !v)}>
          {novo ? 'fechar' : '+ carro'}
        </button>
      </div>

      {carros.data && carros.data.length > 0 && (
        <ul className="mt-2 divide-y divide-edge">
          {carros.data.map((v) => (
            <li key={v.id} className="flex items-center justify-between gap-3 py-2 text-sm">
              <div className="min-w-0">
                <p className="truncate font-semibold">
                  {v.apelido || [v.montadora, v.modelo].filter(Boolean).join(' ') || v.placa}
                </p>
                <p className="text-xs text-dim">
                  {[v.placa, [v.montadora, v.modelo].filter(Boolean).join(' ') || null,
                    v.ano ? String(v.ano) : null, v.motorizacao]
                    .filter(Boolean).join(' · ')}
                </p>
              </div>
              <button type="button" className="shrink-0 text-xs text-dim underline"
                      disabled={desativar.isPending}
                      onClick={() => desativar.mutate(v.id)}>
                vendeu
              </button>
            </li>
          ))}
        </ul>
      )}
      {carros.data && carros.data.length === 0 && !novo && (
        <p className="mt-1 text-xs text-mute">
          Nenhum carro cadastrado. Saber o que o cliente tem é o que permite
          oferecer a peça antes de ele pedir.
        </p>
      )}

      {novo && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="rotulo" htmlFor={`pl-${customerId}`}>Placa (opcional)</label>
              <input id={`pl-${customerId}`} className="campo py-2 font-mono uppercase"
                     placeholder="ABC1D23" value={f.placa}
                     onChange={(e) => set('placa', e.target.value)} />
            </div>
            <div>
              <label className="rotulo" htmlFor={`ap-${customerId}`}>Apelido</label>
              <input id={`ap-${customerId}`} className="campo py-2"
                     placeholder="o Volvo do pátio" value={f.apelido}
                     onChange={(e) => set('apelido', e.target.value)} />
            </div>
            <div>
              <label className="rotulo" htmlFor={`mo-${customerId}`}>Montadora</label>
              <input id={`mo-${customerId}`} className="campo py-2" placeholder="Fiat"
                     value={f.montadora} onChange={(e) => set('montadora', e.target.value)} />
            </div>
            <div>
              <label className="rotulo" htmlFor={`md-${customerId}`}>Modelo</label>
              <input id={`md-${customerId}`} className="campo py-2" placeholder="Palio"
                     value={f.modelo} onChange={(e) => set('modelo', e.target.value)} />
            </div>
            <div>
              <label className="rotulo" htmlFor={`an-${customerId}`}>Ano</label>
              <input id={`an-${customerId}`} className="campo py-2" inputMode="numeric"
                     placeholder="2012" value={f.ano}
                     onChange={(e) => set('ano', e.target.value)} />
            </div>
            <div>
              <label className="rotulo" htmlFor={`mt-${customerId}`}>Motor</label>
              <input id={`mt-${customerId}`} className="campo py-2" placeholder="1.0 8v"
                     value={f.motorizacao} onChange={(e) => set('motorizacao', e.target.value)} />
            </div>
          </div>
          {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
          <button type="button" className="botao-primario w-full py-2 text-sm disabled:opacity-50"
                  disabled={salvar.isPending || (!f.placa.trim() && !f.modelo.trim())}
                  onClick={() => salvar.mutate()}>
            {salvar.isPending ? 'Salvando…' : 'Salvar carro'}
          </button>
        </div>
      )}
      {desativar.error ? <Aviso tipo="erro">{mensagemDeErro(desativar.error)}</Aviso> : null}
    </section>
  );
}
